import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowRight,
    BadgeCheck,
    BookOpen,
    Building2,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    ClipboardCheck,
    FileText,
    GraduationCap,
    Layers,
    LineChart,
    MapPin,
    ShieldCheck,
    Sparkles,
    Target,
    X,
} from "lucide-react";

/**
 * UniThink — India Admission Guidance Landing Page
 * React + Tailwind + (optional) Framer Motion animations
 */

const cx = (...classes) => classes.filter(Boolean).join(" ");

const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.55, delay: 0.06 * i, ease: [0.22, 1, 0.36, 1] },
    }),
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.98 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, scale: 0.98, transition: { duration: 0.2 } },
};

function useScrollSpy(ids = []) {
    const [active, setActive] = useState(ids[0] || "");
    useEffect(() => {
        const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
        if (!els.length) return;

        const obs = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0));
                if (visible[0]?.target?.id) setActive(visible[0].target.id);
            },
            { root: null, rootMargin: "-35% 0px -55% 0px", threshold: [0.08, 0.12, 0.2, 0.35] }
        );

        els.forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, [ids.join("|")]);

    return active;
}

function Pill({ children, icon: Icon }) {
    return (
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-sm text-slate-700 shadow-sm backdrop-blur">
            {Icon ? <Icon className="h-4 w-4 text-slate-700" /> : null}
            {children}
        </span>
    );
}

function SectionTitle({ kicker, title, subtitle }) {
    return (
        <div className="mx-auto max-w-3xl text-center">
            {kicker ? (
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/60 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm">
                    <Sparkles className="h-3.5 w-3.5" />
                    {kicker}
                </div>
            ) : null}
            <h2 className="text-balance text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                {title}
            </h2>
            {subtitle ? (
                <p className="mt-3 text-pretty text-slate-600">{subtitle}</p>
            ) : null}
        </div>
    );
}

function Card({ className, children }) {
    return (
        <div
            className={cx(
                "rounded-2xl border border-slate-200 bg-white shadow-sm",
                "transition hover:shadow-md",
                className
            )}
        >
            {children}
        </div>
    );
}

function IconBadge({ icon: Icon, title, desc }) {
    return (
        <div className="flex gap-3">
            <div className="mt-0.5 flex h-10 w-10 flex-none items-center justify-center rounded-xl border border-slate-200 bg-slate-50">
                <Icon className="h-5 w-5 text-slate-900" />
            </div>
            <div>
                <div className="font-medium text-slate-900">{title}</div>
                <div className="text-sm text-slate-600">{desc}</div>
            </div>
        </div>
    );
}

function FAQItem({ q, a, open, onToggle }) {
    return (
        <Card className="overflow-hidden">
            <button
                onClick={onToggle}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
            >
                <span className="font-medium text-slate-900">{q}</span>
                {open ? (
                    <ChevronUp className="h-5 w-5 text-slate-600" />
                ) : (
                    <ChevronDown className="h-5 w-5 text-slate-600" />
                )}
            </button>
            <AnimatePresence initial={false}>
                {open ? (
                    <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="border-t border-slate-200"
                    >
                        <div className="px-5 py-4 text-sm leading-relaxed text-slate-600">{a}</div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </Card>
    );
}

function Modal({ open, onClose, title, children }) {
    return (
        <AnimatePresence>
            {open ? (
                <motion.div
                    className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <div
                        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    <motion.div
                        variants={scaleIn}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                        className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
                        role="dialog"
                        aria-modal="true"
                        aria-label={title}
                    >
                        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                            <div className="font-semibold text-slate-900">{title}</div>
                            <button
                                onClick={onClose}
                                className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700 transition hover:bg-slate-50"
                                aria-label="Close"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="px-5 py-5">{children}</div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

function Toast({ show, message, onClose }) {
    return (
        <AnimatePresence>
            {show ? (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.25 }}
                    className="fixed bottom-4 left-1/2 z-[60] w-[min(560px,calc(100%-2rem))] -translate-x-1/2"
                >
                    <div className="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 rounded-xl bg-slate-900 p-2 text-white">
                                <CheckCircle2 className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-medium text-slate-900">Submitted.</div>
                                <div className="text-sm text-slate-600">{message}</div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                        >
                            Close
                        </button>
                    </div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

export default function UniThinkIndiaAdmissionGuidanceLanding() {
    const sectionIds = useMemo(
        () => ["overview", "included", "how", "verification", "pricing", "faqs"],
        []
    );
    const active = useScrollSpy(sectionIds);

    const [openModal, setOpenModal] = useState(false);
    const [toast, setToast] = useState(false);
    const [faqOpen, setFaqOpen] = useState(0);

    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [level, setLevel] = useState("Class 12");
    const [cities, setCities] = useState("");
    const [notes, setNotes] = useState("");

    const handlePayment = () => {
        if (!name || !phone || !email) {
            alert("Please fill all required details (Name, Phone, Email).");
            return;
        }
        const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY,
            amount: 2950000, // ₹25,000 in paise
            currency: "INR",
            name: "UniThink",
            description: "India Admission Guidance",
            handler: function (response) {
                // Send data to Google Sheets
                fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                    method: "POST",
                    mode: "no-cors",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        name: name,
                        phone: phone,
                        level: level,
                        cities: cities,
                        notes: notes,
                        payment_status: "Paid",
                        paymentId: response.razorpay_payment_id,
                        service: "India Admission Guidance",
                        amount: 29500
                    }),
                }).finally(() => {
                    alert("Payment Successful! We have received your request.");
                    window.location.href = "/";
                });
            },
            prefill: {
                name: name,
                contact: phone,
            },
            theme: { color: "#0f172a" },
            modal: {
                ondismiss: function () {
                    fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                        method: "POST",
                        mode: "no-cors",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            name: name,
                            phone: phone,
                            level: level,
                            cities: cities,
                            notes: notes,
                            payment_status: "Abandoned",
                            paymentId: "N/A",
                            service: "India Admission Guidance",
                            amount: 29500
                        }),
                    });
                }
            }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
            fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                method: "POST",
                mode: "no-cors",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: name,
                    phone: phone,
                    level: level,
                    cities: cities,
                    notes: notes,
                    payment_status: "Payment Failed",
                    paymentId: response.error.metadata.payment_id || "N/A",
                    service: "India Admission Guidance",
                    amount: 29500
                }),
            });
        });
        rzp.open();
    };

    const nav = [
        { id: "overview", label: "Overview" },
        { id: "included", label: "Included" },
        { id: "how", label: "How it Works" },
        { id: "verification", label: "Verification" },
        { id: "pricing", label: "Pricing" },
        { id: "faqs", label: "FAQs" },
    ];

    const faqs = [
        {
            q: "Is this service only for top-tier colleges?",
            a: "No. The shortlist is built around fit, feasibility, and your goals.",
        },
        {
            q: "Do you help with entrance exam planning?",
            a: "Where applicable, yes—guidance is provided as part of the support.",
        },
        {
            q: "Will you complete applications for me?",
            a: "UniThink provides application assistance and guidance; exact execution support can be clarified in scope.",
        },
        {
            q: "Do you verify college legitimacy?",
            a: "We encourage verification using official sources (including UGC resources) and incorporate checks into the advising process.",
        },
        {
            q: "Does this include scholarship support?",
            a: "Scholarship guidance can be discussed based on institution and eligibility.",
        },
        {
            q: "Do you guarantee admission?",
            a: "No. Admission decisions depend on institution criteria and competitiveness.",
        },
        {
            q: "What if I want to apply to more than 10 colleges?",
            a: "Additional colleges can be supported through add-ons or expanded scope.",
        },
        {
            q: "Can parents be part of the counselling?",
            a: "Yes, parent involvement is often helpful for decision alignment.",
        },
    ];

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="min-h-screen bg-white text-slate-900">
            {/* Top glow */}
            <div className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-80 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(15,23,42,0.08)_0%,rgba(15,23,42,0)_70%)]" />

            {/* Header */}
            <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">



                    <nav className="hidden items-center gap-1 lg:flex">
                        {nav.map((n) => (
                            <button
                                key={n.id}
                                onClick={() => scrollTo(n.id)}
                                className={cx(
                                    "rounded-xl px-3 py-2 text-sm transition",
                                    active === n.id
                                        ? "bg-slate-900 text-white"
                                        : "text-slate-700 hover:bg-slate-100"
                                )}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => scrollTo("pricing")}
                            className="hidden rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50 sm:inline-flex"
                        >
                            View Pricing
                        </button>
                        <button
                            onClick={() => setOpenModal(true)}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                        >
                            Start Admission Process <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <main>
                <section className="relative">
                    <div className="mx-auto max-w-6xl px-4 pb-10 pt-10 sm:px-6 sm:pb-14 sm:pt-14">
                        <div className="grid items-start gap-10 lg:grid-cols-[1.2fr_0.8fr]">
                            <div>
                                <motion.div
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(0)}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm"
                                >
                                    <BadgeCheck className="h-4 w-4" /> Structured + India-focused admissions support
                                </motion.div>

                                <motion.h1
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(1)}
                                    className="mt-4 text-balance text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
                                >
                                    India Admission Guidance
                                </motion.h1>

                                <motion.p
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(2)}
                                    className="mt-4 text-pretty text-slate-600"
                                >
                                    Structured support for selecting the right course and college in India—shortlisting, admissions execution guidance,
                                    and documentation support.
                                </motion.p>

                                <motion.div
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(3)}
                                    className="mt-6 flex flex-wrap gap-2"
                                >
                                    <Pill icon={Target}>Course + college shortlisting</Pill>
                                    <Pill icon={FileText}>Docs + admissions guidance</Pill>
                                    <Pill icon={GraduationCap}>Entrance exam guidance</Pill>
                                    <Pill icon={Layers}>Up to 10 colleges / cycle</Pill>
                                </motion.div>

                                <motion.div
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(4)}
                                    className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center"
                                >
                                    <button
                                        onClick={() => setOpenModal(true)}
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                    >
                                        Start Admission Process <ArrowRight className="h-5 w-5" />
                                    </button>
                                    <button
                                        onClick={() => scrollTo("included")}
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50"
                                    >
                                        What’s Included <ChevronDown className="h-5 w-5" />
                                    </button>
                                    <div className="text-sm text-slate-500 sm:ml-2">
                                        Covers up to <span className="font-semibold text-slate-700">10 colleges</span> per cycle.
                                    </div>
                                </motion.div>
                            </div>

                            {/* Credibility + verification panel */}
                            <motion.div
                                variants={fadeUp}
                                initial="hidden"
                                animate={fadeUp.show(2)}
                                className="lg:pt-3"
                            >
                                <Card className="overflow-hidden">
                                    <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                                        <div className="flex items-center justify-between">
                                            <div className="font-semibold">Coverage + Safety</div>
                                            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
                                                <ShieldCheck className="h-3.5 w-3.5" /> Verification-led
                                            </span>
                                        </div>
                                        <p className="mt-1 text-sm text-slate-600">Up to 10 colleges per admission cycle.</p>
                                    </div>
                                    <div className="space-y-4 px-5 py-5">
                                        <IconBadge
                                            icon={Target}
                                            title="Reach / Match / Safe tiers"
                                            desc="Shortlist prioritised so you don’t gamble your entire year."
                                        />
                                        <IconBadge
                                            icon={ClipboardCheck}
                                            title="Document checklist discipline"
                                            desc="Deadlines + docs managed with clarity (less stress for families)."
                                        />
                                        <IconBadge
                                            icon={ShieldCheck}
                                            title="Legitimacy awareness"
                                            desc="We encourage verification using official sources (including UGC resources)."
                                        />
                                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                            <div className="text-sm font-semibold text-slate-900">Why this matters</div>
                                            <p className="mt-1 text-sm text-slate-600">
                                                India’s system is huge — good guidance = fewer mistakes, better fit, and calmer decision-making.
                                            </p>
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Overview / Problem context */}
                <section id="overview" className="border-t border-slate-200 bg-slate-50/60">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="Why guidance matters"
                            title="India admissions are high-volume — clarity beats chaos"
                            subtitle="Large system, uneven access, and misinformation risk means verification and fit are non-negotiable."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-3">
                            <Card className="p-6">
                                <div className="flex items-start gap-3">
                                    <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                        <Building2 className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">Massive system</div>
                                        <p className="mt-1 text-sm text-slate-600">
                                            AISHE 2021–22 reports 1,168 universities/university-level institutions and 45,473 colleges with ~4.33 crore enrolment.
                                        </p>
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-start gap-3">
                                    <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                        <LineChart className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">Access is uneven</div>
                                        <p className="mt-1 text-sm text-slate-600">
                                            AISHE reports an all-India GER of 28.4 (ages 18–23) in 2021–22.
                                        </p>
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-start gap-3">
                                    <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                        <ShieldCheck className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">Misinformation risk</div>
                                        <p className="mt-1 text-sm text-slate-600">
                                            UGC publishes an official list of “fake universities” and state-wise details—verification matters.
                                        </p>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        <div className="mt-8 grid gap-6 lg:grid-cols-2">
                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <Sparkles className="h-4 w-4" /> What this service does
                                </div>
                                <p className="mt-2 text-sm text-slate-600">
                                    This service helps students and parents make a strong India-focused admission plan: identify the right-fit course
                                    and colleges, reduce uncertainty, and manage the documentation and admission steps with clarity.
                                </p>
                            </Card>
                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <ShieldCheck className="h-4 w-4" /> UniThink vibe (simple)
                                </div>
                                <p className="mt-2 text-sm text-slate-600">
                                    Fit first. Verification always. No random “WhatsApp forwards” as admissions strategy.
                                </p>
                            </Card>
                        </div>
                    </div>
                </section>

                {/* Included */}
                <section id="included" className="bg-white">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="What’s included"
                            title="Shortlisting + docs + decision support (all in one)"
                            subtitle="Designed for families who want clarity and control through the admission season."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-3">
                            {[
                                {
                                    icon: Target,
                                    title: "Course + college shortlisting",
                                    desc: "Build a shortlist aligned to academics, goals, and constraints.",
                                },
                                {
                                    icon: ClipboardCheck,
                                    title: "Admissions + application guidance",
                                    desc: "Execution support clarified in scope—no guesswork.",
                                },
                                {
                                    icon: GraduationCap,
                                    title: "Entrance exam guidance",
                                    desc: "Where applicable, support for planning and requirements.",
                                },
                                {
                                    icon: FileText,
                                    title: "Documentation support",
                                    desc: "Checklist discipline so you don’t lose time in missing docs.",
                                },
                                {
                                    icon: Layers,
                                    title: "Decision-stage counselling",
                                    desc: "Evaluate trade-offs when offers come in.",
                                },
                                {
                                    icon: ShieldCheck,
                                    title: "Verification mindset",
                                    desc: "Encourage checks via official sources (including UGC resources).",
                                },
                            ].map((it, idx) => (
                                <motion.div
                                    key={it.title}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView={fadeUp.show(idx)}
                                    viewport={{ once: true, amount: 0.25 }}
                                >
                                    <Card className="p-6">
                                        <div className="flex items-start gap-3">
                                            <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                                <it.icon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-slate-900">{it.title}</div>
                                                <p className="mt-1 text-sm text-slate-600">{it.desc}</p>
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                            <button
                                onClick={() => setOpenModal(true)}
                                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                            >
                                Start Admission Process <ArrowRight className="h-5 w-5" />
                            </button>
                            <button
                                onClick={() => scrollTo("how")}
                                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50"
                            >
                                See How It Works <ChevronDown className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </section>

                {/* How it works */}
                <section id="how" className="border-t border-slate-200 bg-slate-50/60">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="How it works"
                            title="Four steps. Calm admission season."
                            subtitle="A structured, repeatable process for India-focused admissions."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-4">
                            {[
                                {
                                    icon: ClipboardCheck,
                                    title: "Step 1: Profile + preference intake",
                                    desc: "Academics, location, budget, target branches.",
                                },
                                {
                                    icon: Target,
                                    title: "Step 2: Shortlist + prioritisation",
                                    desc: "Reach / Match / Safe tiers.",
                                },
                                {
                                    icon: FileText,
                                    title: "Step 3: Docs + application planning",
                                    desc: "Checklist + deadlines.",
                                },
                                {
                                    icon: Layers,
                                    title: "Step 4: Evaluate offers + decide",
                                    desc: "Trade-offs and final decision guidance.",
                                },
                            ].map((it, idx) => (
                                <motion.div
                                    key={it.title}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView={fadeUp.show(idx)}
                                    viewport={{ once: true, amount: 0.25 }}
                                >
                                    <Card className="p-6">
                                        <div className="flex items-start gap-3">
                                            <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                                <it.icon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-slate-900">{it.title}</div>
                                                <p className="mt-1 text-sm text-slate-600">{it.desc}</p>
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-10 grid gap-6 lg:grid-cols-2">
                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <CheckCircle2 className="h-4 w-4" /> Outcomes you can expect
                                </div>
                                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                                    {[
                                        "A shortlist aligned to academics, goals, and constraints",
                                        "Reduced risk of applying to unsuitable or unrecognised institutions",
                                        "Better deadline control and documentation readiness",
                                        "Clearer decision-making when multiple offers are available",
                                        "Reduced stress for students and families during the admission season",
                                    ].map((x) => (
                                        <li key={x} className="flex gap-2">
                                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
                                            <span>{x}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <BookOpen className="h-4 w-4" /> Explore related services
                                </div>
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    {[
                                        "Career Clarity Session",
                                        "Comprehensive Counselling Program",
                                        "Application & Admission Support (multi-country)",
                                    ].map((s) => (
                                        <div
                                            key={s}
                                            className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 transition hover:bg-slate-50"
                                        >
                                            <div className="font-semibold text-slate-900">{s}</div>

                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </div>

                        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
                            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                                <div>
                                    <div className="text-lg font-semibold text-slate-900">Want a shortlist that actually makes sense?</div>
                                    <div className="mt-1 text-sm text-slate-600">
                                        We’ll build it with tiers + verification.
                                    </div>
                                </div>
                                <button
                                    onClick={() => setOpenModal(true)}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                >
                                    Start Admission Process <ArrowRight className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Verification panel */}
                <section id="verification" className="border-t border-slate-200 bg-white">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="Verification"
                            title="Avoid misinformation. Verify like an adult."
                            subtitle="We encourage official checks (including UGC resources) as part of decision hygiene."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-2">
                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <ShieldCheck className="h-4 w-4" /> What we do
                                </div>
                                <p className="mt-2 text-sm text-slate-600">
                                    We incorporate verification prompts into the shortlist process so families don’t rely on random claims.
                                </p>
                                <div className="mt-5 space-y-2 text-sm text-slate-600">
                                    {[
                                        "Check recognition/legitimacy from official sources",
                                        "Validate program + campus details",
                                        "Confirm admission requirements + deadlines",
                                        "Cross-check fee structures and refundable policies",
                                    ].map((x) => (
                                        <div key={x} className="flex gap-2">
                                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
                                            <span>{x}</span>
                                        </div>
                                    ))}
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <MapPin className="h-4 w-4" /> What you bring
                                </div>
                                <p className="mt-2 text-sm text-slate-600">
                                    Your constraints (budget, location, branch preferences) + honest goals. We’ll convert that into a clear plan.
                                </p>
                                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                                    {["Budget range", "Preferred cities", "Target branches", "Exam scores", "Category/quota", "Family priorities"].map(
                                        (x) => (
                                            <div key={x} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                                                <div className="font-semibold text-slate-900">{x}</div>

                                            </div>
                                        )
                                    )}
                                </div>
                            </Card>
                        </div>
                    </div>
                </section>

                {/* Pricing */}
                <section id="pricing" className="border-t border-slate-200 bg-slate-50/60">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="Price & positioning"
                            title="₹25,000 + 18% GST per cycle (up to 10 colleges)"
                            subtitle="Best for India-focused admissions: shortlisting + documentation + decision guidance."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1fr]">
                            <Card className="p-6">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                                            <ClipboardCheck className="h-4 w-4" /> Admission cycle
                                        </div>
                                        <div className="mt-4 text-3xl font-semibold tracking-tight text-slate-900">₹25,000 + 18% GST</div>
                                        <div className="mt-1 text-sm text-slate-600">per cycle (up to 10 colleges)</div>
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-white p-3">
                                        <div className="text-xs font-semibold text-slate-600">Coverage</div>
                                        <div className="mt-1 text-sm font-semibold text-slate-900">Up to 10 colleges</div>
                                    </div>
                                </div>

                                <div className="mt-6 space-y-2 text-sm text-slate-600">
                                    {[
                                        "Course + college shortlist (tiered)",
                                        "Admissions + documentation guidance",
                                        "Entrance-exam guidance (where applicable)",
                                        "Offer evaluation + final decision counselling",
                                        "Verification prompts using official sources",
                                    ].map((x) => (
                                        <div key={x} className="flex gap-2">
                                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
                                            <span>{x}</span>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={() => setOpenModal(true)}
                                    className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                >
                                    Start Admission Process <ArrowRight className="h-5 w-5" />
                                </button>

                                <div className="mt-3 text-center text-xs text-slate-500">
                                    Add your policy: timelines, rescheduling, and add-on pricing.
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="text-sm font-semibold text-slate-900">Recommended reading</div>
                                <div className="mt-4 space-y-3">
                                    {[
                                        "How to shortlist colleges in India: fit, fees, and future outcomes",
                                        "Avoiding admission season misinformation: what to verify",
                                        "Course selection mistakes that cost students years",
                                    ].map((x) => (
                                        <div
                                            key={x}
                                            className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 transition hover:bg-slate-50"
                                        >
                                            <div className="font-semibold text-slate-900">{x}</div>
                                            <div className="mt-1 text-xs text-slate-500">Coming soon</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
                                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                        <BookOpen className="h-4 w-4" /> Still unsure about direction?
                                    </div>

                                    <button
                                        onClick={() => scrollTo("overview")}
                                        className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                                    >
                                        See why guidance matters <ArrowRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </Card>
                        </div>
                    </div>
                </section >

                {/* FAQs */}
                < section id="faqs" className="border-t border-slate-200 bg-white" >
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="FAQs"
                            title="Quick answers for parents + students"
                            subtitle="So everyone stays aligned during admission season."
                        />

                        <div className="mt-10 grid gap-4 lg:grid-cols-2">
                            {faqs.map((f, i) => (
                                <FAQItem
                                    key={f.q}
                                    q={f.q}
                                    a={f.a}
                                    open={faqOpen === i}
                                    onToggle={() => setFaqOpen((p) => (p === i ? -1 : i))}
                                />
                            ))}
                        </div>

                        <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                                <div>
                                    <div className="text-lg font-semibold text-slate-900">
                                        Make your India admissions decisions with clarity and verification.
                                    </div>
                                    <div className="mt-1 text-sm text-slate-600">Start your India Admission Guidance today.</div>
                                </div>
                                <button
                                    onClick={() => setOpenModal(true)}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                >
                                    Start Admission Process <ArrowRight className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </section >

            </main >

            {/* Modal */}
            < Modal open={openModal} onClose={() => setOpenModal(false)
            } title="Start Admission Process — Get Started" >
                <div className="grid gap-4 sm:grid-cols-2">
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Student Name</span>
                        <input
                            value={name} onChange={(e) => setName(e.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., Riya"
                        />
                    </label>
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Phone Number</span>
                        <input
                            value={phone} onChange={(e) => setPhone(e.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., +91 XXXXX XXXXX"
                        />
                    </label>
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Email Address</span>
                        <input
                            type="email"
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., student@gmail.com"
                        />
                    </label>
                    <label className="grid gap-1 sm:col-span-2">
                        <span className="text-xs font-medium text-slate-700">Current level</span>
                        <select value={level} onChange={(e) => setLevel(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400">
                            <option>Class 12</option>
                            <option>Drop year</option>
                            <option>UG (in progress)</option>
                            <option>PG aspirant</option>
                        </select>
                    </label>
                    <label className="grid gap-1 sm:col-span-2">
                        <span className="text-xs font-medium text-slate-700">Preferred states/cities</span>
                        <input
                            value={cities} onChange={(e) => setCities(e.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., Delhi NCR, Pune, Bangalore"
                        />
                    </label>
                    <label className="grid gap-1 sm:col-span-2">
                        <span className="text-xs font-medium text-slate-700">Notes</span>
                        <textarea
                            value={notes} onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="Course interests, budget, target branches, exam scores, category/quota, etc."
                        />
                    </label>
                </div>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-slate-500">Next: connect this form to WhatsApp / Google Sheets / Razorpay.</div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setOpenModal(false)}
                            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                        >
                            Not now
                        </button>
                        <button
                            onClick={handlePayment}
                            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:translate-y-[-1px]"
                        >
                            Continue <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </Modal >

            <Toast
                show={toast}
                message="We captured your request (demo). Next, connect this to your actual intake workflow."
                onClose={() => setToast(false)}
            />
        </div >
    );
}
